import { useEffect, useRef } from "react";
import { useOutcomeStatusAndOdds } from "@/features/betting/hooks/useOutcomeStatusAndOdds";
import { usePreferences } from "hooks/firestore/user/usePreferences";
import { useUserToken } from "@/features/auth/hooks/useUserToken";
import { useBetslipStore } from "@/features/betslip/stores/betslipStore";
import { fetchSEMOdds } from "@/features/betslip/services/semPricing";
import { deriveOutcomeStatus } from "@/features/betslip/utils/marketStatus";
import {
  isLiveViewOnly,
  getEffectiveOdds,
} from "utilities/sharedBettingUtilities";
import type { BettingType, EventStatus } from "hooks/firestore/betting/types";
import type {
  MarketStatus,
  OddsPreference,
  SemSelection,
} from "@/features/betslip/types";
import type { UpdatesSyncTarget } from "@/features/betslip/stores/types";

const writeMarketStatus = (
  outcomeId: string,
  status: MarketStatus | undefined,
): void => {
  if (!status) return;
  useBetslipStore.getState().setMarketStatus(outcomeId, status);
};

// Outlives the trading status - a suspended live market still reads Live.
const writeMarketBettingType = (
  outcomeId: string,
  bettingType: BettingType | undefined,
): void => {
  if (!bettingType) return;
  useBetslipStore.getState().setMarketBettingType(outcomeId, bettingType);
};

// The event's own lifecycle, for the clamp - nothing to do with permissions.
const writeEventStatus = (
  eventId: string,
  outcome: { eventStatus?: EventStatus; mainMarketStatus?: MarketStatus },
): void => {
  const status = outcome.eventStatus;
  if (!status) return;
  // The main market is what sets the event's lifecycle, the same reading the
  // match list uses - so a live main market on an ACTIVE event still says
  // LIVE. Deliberately not this outcome's own market: `eventStatuses` is one
  // entry per event, and two selections on it would otherwise write different
  // values and flip the entry on every tick.
  const effective =
    status === "ACTIVE" && outcome.mainMarketStatus === "LIVE"
      ? "LIVE"
      : status;
  useBetslipStore.getState().setEventStatus(eventId, effective);
};

const writeTopLevelOdds = (
  outcomeId: string,
  newOdds: number,
  acceptOdds: OddsPreference,
): void => {
  const state = useBetslipStore.getState();
  const selection = state.selections[outcomeId];
  if (!selection) return;
  // Unchanged price with nothing pending is a no-op. When a change is still
  // pending, let setOddsChange run so it clears the moot entry (the market
  // moved back to the committed price).
  if (
    selection.odds === newOdds &&
    state.oddsChanges[outcomeId] === undefined
  ) {
    return;
  }
  state.setOddsChange(outcomeId, newOdds, acceptOdds);
};

const repriceSemBundle = async (
  sem: SemSelection,
  token: string,
  acceptOdds: OddsPreference,
): Promise<void> => {
  const outcomeIds = sem.legs.map((leg) => leg.outcomeId);
  const result = await fetchSEMOdds({ outcomeIds, token });
  if (!result.ok) return; // Silent: re-price failures don't disturb the existing SEM.
  const stillInSlip = useBetslipStore.getState().selections[sem.outcomeId];
  if (!stillInSlip) return;
  const { setOddsChange, setSemRedundantLegs } = useBetslipStore.getState();
  setOddsChange(sem.outcomeId, result.data.odds, acceptOdds);
  setSemRedundantLegs(sem.outcomeId, result.data.redundantOutcomeIds);
};

/**
 * Feeds the updates slice for one outcome. Market status, betting type and
 * event status always sync; the price only when `trackOdds` says this user
 * could act on it.
 *
 * Top-level selection: odds drift dispatches setOddsChange(outcomeId).
 * SEM leg (pass `sem`): status writes target legId; odds drift triggers
 * `fetchSEMOdds` re-price, then setOddsChange(sem.outcomeId). First
 * observation seeds only (the SEM was already priced when added).
 *
 * Mid-submission, showing results, or under an overlay: only the price
 * pauses (don't move it while the user's deciding). Status keeps syncing.
 *
 * `OutcomeSubscriptions` fans SEMs out per leg.
 */
export const useUpdatesSync = ({
  outcomeId,
  eventId,
  statusId,
  trackOdds,
  sem,
}: UpdatesSyncTarget) => {
  const { outcome, isLoading, eventStatus, canViewLiveBets } =
    useOutcomeStatusAndOdds(outcomeId, eventId, sem ? "sem" : "all");
  // Keyed by slip id: a SEM's price sits on the bundle, not the leg.
  const slipId = sem?.outcomeId ?? statusId;
  const isLiveViewOnlySelection = useBetslipStore(
    (state) => state.liveViewOnly[slipId],
  );
  const { preferences } = usePreferences();
  // Default to MANUAL while preferences load; never auto-accept before the first read.
  const acceptOdds: OddsPreference = preferences.acceptOdds ?? "MANUAL";
  const token = useUserToken();
  const hasOutcome = outcome !== undefined;
  const isSyncPaused = useBetslipStore(
    (state) =>
      state.submission.phase === "submitting" ||
      state.isReviewing ||
      state.overlay.type !== "none",
  );
  // Baseline for SEM drift detection. It only advances when an update is
  // actually consumed, so a move that happens during a pause still
  // triggers a re-price once the pause lifts.
  const lastProcessedOddsRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (isLoading) return;

    // No market for this outcome once the event has loaded means the feed has
    // dropped it - deactivated, or hidden because it's live and this user has
    // no live access. Nothing else writes here, so without this the card keeps
    // showing the price it was added at.
    if (!outcome) {
      // An event can read as loaded before it has anything in it, and a missing
      // market means nothing without the event around it.
      if (!eventStatus) return;
      // All that's left to go on, and what view-only derives from.
      writeEventStatus(eventId, { eventStatus });
      // Hidden isn't gone: view-only covers a live event, so anything else
      // here really has been withdrawn.
      if (!isLiveViewOnly(canViewLiveBets, eventStatus)) {
        writeMarketStatus(statusId, "UNAVAILABLE");
      }
      return;
    }

    // Derive the effective status from the outcome (result/active/odds), not
    // just the market status, so a scratched or deactivated runner reads as
    // inactive. `kind` lives on the parent selection (the SEM for a leg).
    const sourceType =
      useBetslipStore.getState().selections[slipId]?.kind ?? "";
    writeMarketStatus(statusId, deriveOutcomeStatus(outcome, sourceType));
    writeMarketBettingType(statusId, outcome.bettingType);
    writeEventStatus(eventId, outcome);

    // Price pauses here, status writes above already happened.
    if (isSyncPaused) return;

    // No price to show and none to accept - drop any pending change.
    if (isLiveViewOnlySelection) {
      useBetslipStore.getState().discardOddsChange(slipId);
    }

    if (!trackOdds) return;

    const effectiveOdds = getEffectiveOdds({
      odds: outcome.odds,
      closingOdds: outcome.closingOdds,
      active: outcome.active || undefined,
      marketStatus: outcome.marketStatus,
      isLiveViewOnly: outcome.isLiveViewOnly,
    });
    if (effectiveOdds === undefined) return;

    if (!sem) {
      writeTopLevelOdds(outcomeId, effectiveOdds, acceptOdds);
      return;
    }

    // SEM leg: re-price the bundle when this leg's odds move between
    // processed observations.
    const baseline = lastProcessedOddsRef.current;
    lastProcessedOddsRef.current = outcome.odds;
    if (typeof baseline !== "number") return;
    if (baseline === outcome.odds) return;
    if (!token) return;

    void repriceSemBundle(sem, token, acceptOdds);
  }, [
    outcomeId,
    statusId,
    slipId,
    isLiveViewOnlySelection,
    trackOdds,
    eventId,
    sem?.outcomeId,
    outcome?.odds,
    outcome?.closingOdds,
    outcome?.marketStatus,
    outcome?.bettingType,
    outcome?.eventStatus,
    outcome?.active,
    outcome?.result,
    hasOutcome,
    eventStatus,
    canViewLiveBets,
    isLoading,
    acceptOdds,
    token,
    isSyncPaused,
  ]);
};
